import { useEffect, useState, useMemo, useRef } from "react";
import { os, isMobileOrMobileWeb, agent } from "../platform";
import AppStore from "../storage/appStore";
import STRINGS from "../constants/strings";
import { COLORS as AppColors } from "../theme/colors";
import { COLORS as SubmoduleColors } from "../sub/theme/colors";
import { handleLinkingPress } from "../utils/helper";
import ErrorMessage from "../sub/components/ErrorMessage";

// Constants
const BANNER_BOTTOM = 70;
const BANNER_RIGHT_MOBILE = 16;
const BANNER_RIGHT_DESKTOP = 80;
const BANNER_Z_INDEX = 9999;

/**
 * InstallPromptWeb component displays a prompt to install the web app as a PWA or redirects to app stores on mobile devices.
 *
 * - Handles the `beforeinstallprompt` event for PWA installation on supported browsers.
 * - Shows a custom install banner for mobile and desktop users.
 * - On iOS: Redirects to App Store
 * - On Android: Shows "Open App" (deep link) and "Install" (Play Store) buttons
 * - On Desktop: Triggers native PWA install prompt
 * - Allows users to dismiss the install prompt for the session.
 *
 * @component
 * @returns {JSX.Element|null} The install prompt banner, or null if not shown.
 */
const InstallPromptWeb = () => {
  const setWebInstaller = AppStore((state) => state.setWebInstaller);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const mountedRef = useRef(true);

  const isMobile = isMobileOrMobileWeb();
  const userAgent = agent();

  const styles = useMemo(() => getStyles(isMobile), [isMobile]);

  useEffect(() => {
    if (os !== "web") {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Show prompt on ALL web browsers (desktop and mobile)
    setShowPrompt(true);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleClick = async () => {
    if (isMobile) {
      if (userAgent === "ios") {
        handleLinkingPress(STRINGS.appleAppstoreLink);
      } else {
        setErrorMessage("Your device is not supported for direct installation.");
        setShowError(true);
      }
    } else {
      if (deferredPrompt && !isInstalling) {
        setIsInstalling(true);
        try {
          await deferredPrompt.prompt();
          const { outcome } = await deferredPrompt.userChoice;

          if (!mountedRef.current) {
            return;
          }

          if (outcome === "accepted") {
            setWebInstaller({ installed: true });
          }
          setDeferredPrompt(null);
        } catch (error) {
          console.error("Install prompt error:", error);
          if (mountedRef.current) {
            setErrorMessage("Unable to install. Please try again.");
            setShowError(true);
          }
        } finally {
          if (mountedRef.current) {
            setIsInstalling(false);
          }
        }
      } else if (!deferredPrompt) {
        setErrorMessage("To install, tap the browser menu and select 'Add to Home screen'");
        setShowError(true);
      }
    }
  };

  const handleOpenApp = () => {
    window.location = "vbparkfinder://";
  };

  const handleInstallApp = () => {
    handleLinkingPress(STRINGS.googlePlaystoreLink);
  };

  if (!showPrompt) {
    return null;
  }

  const isAndroid = userAgent === "android";
  const label = isMobile ? "Install App" : "Add to Home Screen";

  return (
    <>
      <ErrorMessage
        message={errorMessage}
        showError={showError}
        onClose={() => setShowError(false)}
      />
      <div style={styles.banner}>
        <img src="/assets/icons/icon-96.webp" alt="VB Parkfinder" style={styles.icon} />
        <div style={styles.text}>
          <div style={styles.title}>VB Parkfinder</div>
        </div>
        {isAndroid ? (
          <>
            <button
              style={styles.button}
              onClick={handleOpenApp}
              aria-label="Open VB Parkfinder app"
            >
              Open App
            </button>
            <button
              style={styles.button}
              onClick={handleInstallApp}
              aria-label="Install VB Parkfinder from Play Store"
            >
              Install
            </button>
          </>
        ) : (
          <button
            style={styles.button}
            onClick={handleClick}
            disabled={isInstalling}
            aria-label={label}
          >
            {isInstalling ? "Installing..." : label}
          </button>
        )}
        <button
          style={styles.close}
          onClick={() => {
            setShowPrompt(false);
          }}
          aria-label="Close install prompt"
        >
          ×
        </button>
      </div>
    </>
  );
};

const getStyles = (isMobile) => ({
  banner: {
    position: "fixed",
    bottom: BANNER_BOTTOM,
    right: isMobile ? BANNER_RIGHT_MOBILE : BANNER_RIGHT_DESKTOP,
    backgroundColor: AppColors.primaryVariant,
    border: `1px solid ${AppColors.primary}`,
    borderRadius: 12,
    padding: isMobile ? "12px 16px" : "14px 18px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    zIndex: BANNER_Z_INDEX,
    maxWidth: isMobile ? "90%" : 460,
  },
  icon: {
    width: isMobile ? 48 : 40,
    height: isMobile ? 48 : 40,
    borderRadius: 8,
    marginRight: 12,
  },
  text: {
    flex: 1,
    fontSize: isMobile ? 14 : 13,
    lineHeight: "1.2em",
  },
  title: {
    fontWeight: "bold",
    color: SubmoduleColors.text,
  },
  button: {
    backgroundColor: AppColors.primary,
    color: "white",
    border: "none",
    padding: "8px 14px",
    borderRadius: 6,
    fontSize: 13,
    fontWeight: "bold",
    marginLeft: 8,
    cursor: "pointer",
  },
  close: {
    background: "none",
    border: "none",
    fontSize: 18,
    marginLeft: 10,
    cursor: "pointer",
    color: SubmoduleColors.textSecondary,
  },
});

export default InstallPromptWeb;
